/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useMemo } from 'react';
import { css, keyframes } from '@emotion/react';
import { Instagram, Mail, Menu, X } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import Lightbox from "yet-another-react-lightbox";
/*import Download from "yet-another-react-lightbox/plugins/download";*/
import "yet-another-react-lightbox/styles.css";
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';


// Fade-in animation keyframe
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Reusable Fade-in wrapper component
const FadeIn = ({ children, delay = 0 }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <div
      ref={ref}
      css={css`
        opacity: 0;
        ${inView && css`
          animation: ${fadeIn} 0.8s ease-out ${delay}s forwards;
        `}
      `}
    >
      {children}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navStyles = css`
    position: fixed;
    width: 100%;
    z-index: 50;
    background: rgba(1, 1, 13, 0.6);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: none;
  `;

  const navContainerStyles = css`
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: 1120px) {
      padding: 0 1.5rem;
    }
  `;

  const navContentStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 4rem;

    @media (min-width: 1120px) {
      height: 5rem;
    }
  `;

  const menuStyles = css`
    display: none;
    align-items: center;
    gap: 2rem;

    @media (min-width: 1120px) {
      display: flex;
    }
  `;

  const linkStyles = css`
    font-size: 0.875rem;
    letter-spacing: 0.05em;
    color: #FFFFFF;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #88F9C0;
    }
  `;

  const logoStyles = css`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.75rem;
    font-weight: bold;
    letter-spacing: -0.025em;
    text-decoration: none;
    color: #FFFFFF;

    @media (min-width: 1120px) {
      font-size: 1rem;
    }
  `;

  const mobileMenuButtonStyles = css`
    display: block;
    margin-left: auto;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    color: #FFFFFF;

    @media (min-width: 1120px) {
      display: none;
    }
  `;

  const mobileMenuStyles = css`
    background: transparent;
    backdrop-filter: blur(20px);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding: 1.5rem;

    @media (min-width: 1120px) {
      display: none;
    }
  `;

  const mobileMenuLinksStyles = css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
  `;

  const mobileLinkStyles = css`
    font-size: 0.875rem;
    letter-spacing: 0.05em;
    color: #FFFFFF;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #88F9C0;
    }
  `;

  return (
    <nav css={navStyles}>
      <div css={navContainerStyles}>
        <div css={navContentStyles}>
          {/* Left Menu */}
          <div css={menuStyles}>
            <Link to="/" css={linkStyles}>Home</Link>
            <Link to="/photos" css={linkStyles}>Photo</Link>
            <Link to="/video" css={linkStyles}>Video</Link>
            <Link to="/weddings" css={linkStyles}>Weddings</Link>
            <Link to="/events" css={linkStyles}>Events</Link>
          </div>

          {/* Center Logo */}
          <Link to="/" css={logoStyles}>
            <h1>JAMES LAWRENCE</h1>
          </Link>

          {/* Right Menu */}
          <div css={menuStyles}>
            <Link to="/contact" css={linkStyles}>Contact</Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            css={mobileMenuButtonStyles}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div css={mobileMenuStyles}>
          <div css={mobileMenuLinksStyles}>
            <Link to="/" css={mobileLinkStyles} onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/photos" css={mobileLinkStyles} onClick={() => setMobileMenuOpen(false)}>Photo</Link>
            <Link to="/video" css={mobileLinkStyles} onClick={() => setMobileMenuOpen(false)}>Video</Link>
            <Link to="/weddings" css={mobileLinkStyles} onClick={() => setMobileMenuOpen(false)}>Weddings</Link>
            <Link to="/events" css={mobileLinkStyles} onClick={() => setMobileMenuOpen(false)}>Events</Link>
            <Link to="/contact" css={mobileLinkStyles} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const Hero = () => {
  const [draggingId, setDraggingId] = useState(null);
  const [positions, setPositions] = useState({});
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });

  const images = useMemo(() => [
    { src: '/photos/cycle27.jpg', landscape: false },
    { src: '/photos/photoski.jpg', landscape: false },
    { src: '/photos/horizontal12.jpg', landscape: true },
    { src: '/photos/auto4.jpeg', landscape: false },
    { src: '/photos/life1.jpg', landscape: false },
    { src: '/photos/food1.jpg', landscape: false },
    { src: '/photos/horizontal15.jpg', landscape: true },
    { src: '/photos/cycle19.jpg', landscape: false },
    { src: '/photos/ski6.jpg', landscape: false },
    { src: '/photos/cycle36.jpg', landscape: false },
    { src: '/photos/cycle39.jpg', landscape: false},
    { src: '/photos/auto2.jpeg', landscape: false },
    { src: '/photos/food5.jpg', landscape: false },
    { src: '/photos/cycle0.jpg', landscape: false },
    { src: '/photos/cycle41.jpg', landscape: false},
    { src: '/photos/horizontal7.jpg', landscape: true },
    { src: '/photos/ski3.jpg', landscape: false },
    { src: '/photos/auto5.png', landscape: false },
    { src: '/photos/horizontal1.jpg', landscape: true },
    { src: '/photos/life21.jpg', landscape: false },
    { src: '/photos/auto1.jpeg', landscape: false },
    { src: '/photos/cycle37.jpg', landscape: false },
    { src: '/photos/photofood.jpg', landscape: false },
    { src: '/photos/cycle15.jpg', landscape: false },
    { src: '/photos/cover11.png', landscape: true },
    { src: '/photos/auto6.png', landscape: false },
    { src: '/photos/ski21.jpg', landscape: true},
    { src: '/photos/DSC06225.jpg', landscape: false },
  ], []);

  // Initialize random positions - smaller on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const newPositions = {};
    images.forEach((_, idx) => {
      if (isMobile) {
        newPositions[idx] = {
          x: Math.random() * (window.innerWidth - 100),
          y: Math.random() * (window.innerHeight - 200) + 50
        };
      } else {
        newPositions[idx] = {
          x: Math.random() * (window.innerWidth - 300),
          y: Math.random() * (window.innerHeight - 300) + 100
        };
      }
    });
    setPositions(newPositions);
  }, [images]);

  useEffect(() => {
    const handleWindowResize = () => {
      setPositions(prev => {
        const updated = { ...prev };
        
        Object.keys(updated).forEach(idx => {
          const pos = updated[idx];
          
          // Constrain to window bounds
          pos.x = Math.max(0, Math.min(window.innerWidth - 100, pos.x));
          pos.y = Math.max(0, Math.min(window.innerHeight - 100, pos.y));
        });
        
        return updated;
      });
    };
    
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);


  // Mouse events (desktop)
  const handleMouseDown = (idx, e) => {
    setDraggingId(idx);
    const rect = e.currentTarget.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Touch events (mobile)
  const handleTouchStart = (idx, e) => {
    e.preventDefault();
    setDraggingId(idx);
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    setOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (draggingId === null) return;
    
    const newX = e.clientX - offset.x;
    const newY = e.clientY - offset.y;
    
    // Calculate velocity (change in position)
    setVelocity({
      x: newX - (positions[draggingId]?.x || 0),
      y: newY - (positions[draggingId]?.y || 0)
    });
    
    requestAnimationFrame(() => {
      setPositions(prev => ({
        ...prev,
        [draggingId]: {
          x: newX,
          y: newY
        }
      }));
    });
  };

  const handleTouchMove = (e) => {
    if (draggingId === null) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const newX = touch.clientX - offset.x;
    const newY = touch.clientY - offset.y;
    
    // Calculate velocity (change in position)
    setVelocity({
      x: newX - (positions[draggingId]?.x || 0),
      y: newY - (positions[draggingId]?.y || 0)
    });
    
    requestAnimationFrame(() => {
      setPositions(prev => ({
        ...prev,
        [draggingId]: {
          x: newX,
          y: newY
        }
      }));
    });
  };

  // Apply momentum when released
  const handleMouseUp = () => {
    if (draggingId === null) return;
    
    const friction = 0.95; // How much it slows down (0-1), adjust for different feel
    let currentVelocity = { ...velocity };
    let currentPos = { ...positions[draggingId] };
    let id = draggingId;
    
    const applyMomentum = () => {
      currentVelocity.x *= friction;
  currentVelocity.y *= friction;
  
  currentPos.x += currentVelocity.x;
  currentPos.y += currentVelocity.y;
  
  // Bounce off walls
  const bounceElasticity = 0.6;
  const verticalPadding = 50; // Only for top/bottom
  
  // No padding for sides (x-axis)
  if (currentPos.x < 0) {
    currentPos.x = 0;
    currentVelocity.x *= -bounceElasticity;
  } else if (currentPos.x > window.innerWidth - 100) {
    currentPos.x = window.innerWidth - 100;
    currentVelocity.x *= -bounceElasticity;
  }
  
  // Padding for top/bottom only (y-axis)
  if (currentPos.y < verticalPadding) {
    currentPos.y = verticalPadding;
    currentVelocity.y *= -bounceElasticity;
  } else if (currentPos.y > window.innerHeight - 100 - verticalPadding) {
    currentPos.y = window.innerHeight - 100 - verticalPadding;
    currentVelocity.y *= -bounceElasticity;
  }
      
      setPositions(prev => ({
        ...prev,
        [id]: currentPos
      }));
      
      // Stop when velocity is tiny
      if (Math.abs(currentVelocity.x) > 0.5 || Math.abs(currentVelocity.y) > 0.5) {
        requestAnimationFrame(applyMomentum);
      }
    };
    
    applyMomentum();
    setDraggingId(null);
    setVelocity({ x: 0, y: 0 });
  };

  const sectionStyles = css`
    min-height: 100vh;
    height: 100vh;
    background:
  radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99, 49, 251, 0.35), transparent 70%),
  #01010D;
    position: relative;
    overflow: hidden;
    touch-action: none;
  `;

  const containerStyles = css`
    position: relative;
    width: 100%;
    height: 100%;
  `;

  const draggableImageStyles = (idx) => css`
    position: absolute;
    transform: translate(${positions[idx]?.x ?? 0}px, ${positions[idx]?.y ?? 0}px);
    will-change: transform;
    cursor: ${draggingId === idx ? 'grabbing' : 'grab'};
    user-select: none;
    touch-action: none;
    transition: ${draggingId === idx ? 'none' : 'box-shadow 0.2s'};

    &:hover {
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 768px) {
      width: 90px !important;
      height: ${positions[idx]?.landscape ? '62px' : '146px'} !important;
    }
  `;

  const imageStyles = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 4px;
    pointer-events: none;
  `;

  return (
    <section 
      css={sectionStyles}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
    >
      <div css={containerStyles}>
        {images.map((image, idx) => (
          <div
            key={idx}
            css={draggableImageStyles(idx)}
            style={{
              width: image.landscape ? '300px' : '200px',
              height: image.landscape ? '169px' : '266px',
            }}
            onMouseDown={(e) => handleMouseDown(idx, e)}
            onTouchStart={(e) => handleTouchStart(idx, e)}
          >
            <img
              src={image.src}
              alt={`Portfolio ${idx}`}
              css={imageStyles}
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </section>
  );
};

// Contact Section (links under title)
const Contact = () => {
  const sectionStyles = css`
    padding: 8rem 1rem 3rem;
    background:
  radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99, 49, 251, 0.35), transparent 70%),
  #01010D;

    @media (min-width: 768px) {
      padding: 10rem 1.5rem 5rem;
    }

    @media (min-width: 1024px) {
      padding: 10rem 1.5rem 8rem;
    }
  `;

  const containerStyles = css`
    max-width: 1152px;
    margin: 0 auto;
  `;

  const headerWrapStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const headingStyles = css`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  color: #FFFFFF;
  margin: 0;
`;

const eyebrowStyles = css`
  font-size: 0.75rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #88F9C0;
  margin: 0;
`;

  const linksContainerStyles = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 3rem;

    @media (min-width: 640px) {
      flex-direction: row;
      justify-content: center;
      gap: 2rem;
    }

    @media (min-width: 768px) {
      margin-bottom: 4rem;
    }
  `;

  const linkStyles = css`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.875rem;
    color: #FFFFFF;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #9A9AB5;
    }

    @media (min-width: 768px) {
      font-size: 1rem;
    }

    @media (min-width: 1024px) {
      font-size: 1.125rem;
    }
  `;

  const gridStyles = css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    align-items: center;

    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 3rem;
    }

    @media (min-width: 1024px) {
      gap: 4rem;
    }
  `;

  const imageStyles = css`
    width: 100%;
    aspect-ratio: 3/4;
    object-fit: cover;
  `;

  const paragraphStyles = css`
    color: #FFFFFF;
    font-size: 0.9rem;
    line-height: 1.625;
    margin-bottom: 1rem;

    @media (min-width: 768px) {
      font-size: 1rem;
      margin-bottom: 1.5rem;
    }

    @media (min-width: 1024px) {
      font-size: 1.125rem;
    }
  `;

  return (
    <section css={sectionStyles}>
      <div css={containerStyles}>
        <FadeIn>
          <div css={headerWrapStyles}>
            <h2 css={headingStyles}>Get in touch</h2>
            <p css={eyebrowStyles}>Let's create together</p>
          </div>
          
          {/* Contact Links - directly under title */}
          <div css={linksContainerStyles}>
            <a 
              href="mailto:jamesphotographyco@gmail.com"
              css={linkStyles}
            >
              <Mail size={20} />
              <span>jamesphotographyco@gmail.com</span>
            </a>
            <a 
              href="https://instagram.com/jamesphotographyco"
              target="_blank"
              rel="noopener noreferrer"
              css={linkStyles}
            >
              <Instagram size={20} />
              <span>@jamesphotographyco</span>
            </a>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          <div css={gridStyles}>
            {/* Left: Image */}
            <div>
              <img 
                src='/photos/photoJames.jpg'
                alt="James Lawrence"
                css={imageStyles}
              />
            </div>

            {/* Right: About Text */}
            <div>
              <p css={paragraphStyles}>
                Hi! I'm a photographer, videographer, and social content producer.
              </p>
              <p css={paragraphStyles}>
                I'd love the opportunity to create. I'm into lifestyle, sports, products, events, or anything adventurous. Feel free to reach out, I'd be happy to chat!
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const footerStyles = css`
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding: 1.5rem 1rem;
    background: #01010D;

    @media (min-width: 768px) {
      padding: 2rem 1.5rem;
    }

    @media (min-width: 1024px) {
      padding: 3rem 1.5rem;
    }
  `;

  const containerStyles = css`
    max-width: 1280px;
    margin: 0 auto;
  `;

  const contentStyles = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;

    @media (min-width: 768px) {
      flex-direction: row;
      justify-content: space-between;
      gap: 1rem;
    }
  `;

  const logoStyles = css`
    font-size: 1.125rem;
    font-weight: bold;
    color: #FFFFFF;
    letter-spacing: -0.025em;

    @media (min-width: 768px) {
      font-size: 1.25rem;
    }
  `;

  const copyrightStyles = css`
    color: #9A9AB5;
    font-size: 0.75rem;

    @media (min-width: 768px) {
      font-size: 0.875rem;
    }
  `;

  return (
    <footer css={footerStyles}>
      <div css={containerStyles}>
        <div css={contentStyles}>
          <div>
            <span css={logoStyles}>JAMES LAWRENCE</span>
          </div>
          <p css={copyrightStyles}>
            2026 James Photography Co.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Portfolio Grid Component (reusable)
// PORTFOLIO GRID (PHOTOS PAGE)
const PortfolioGrid = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentCategory, setCurrentCategory] = useState([]);

  const PREVIEW_COUNT = 6;

  const categories = [
    {
      title: 'CYCLE',
      id: 'cycle',
      images: [
        '/photos/cycle15.jpg',
        '/photos/cycle19.jpg',
        '/photos/cycle27.jpg',
        '/photos/cycle0.jpg',
        '/photos/cycle44.jpg',
        '/photos/cycle31.jpg',
        '/photos/cycle18.jpg',
        '/photos/cycle41.jpg',
        '/photos/cycle33.jpg',
        '/photos/cycle25.jpg',
        '/photos/cycle42.jpg',
        '/photos/cycle7.jpg',
        '/photos/cycle40.jpg',
        '/photos/cycle17.jpg',
        '/photos/cycle32.jpg',
        '/photos/cycle43.jpg',
        '/photos/cycle21.jpg',
        '/photos/cycle24.jpg',
        '/photos/cycle9.jpg',
        '/photos/cycle1.jpg',
        '/photos/cycle2.jpg',
        '/photos/cycle3.jpg',
        '/photos/cycle5.jpg',
        '/photos/cycle8.jpg',
        '/photos/cycle35.jpg',
        '/photos/cycle29.jpg',
        '/photos/cycle10.jpg',
        '/photos/cycle26.jpg',
        '/photos/cycle12.jpg',
        '/photos/cycle13.jpg',
        '/photos/cycle4.jpg'
      ]
    },
    {
      title: 'SKI',
      id: 'ski',
      images: [
        '/photos/ski6.jpg',
        '/photos/ski3.jpg',
        '/photos/ski7.jpg',
        '/photos/ski15.jpg',
        '/photos/photoski.jpg',
        '/photos/ski1.jpg',
        '/photos/ski11.jpg',
        '/photos/ski2.jpg',
        '/photos/ski20.jpg',
        '/photos/ski4.jpg',
        '/photos/ski12.jpg',
        '/photos/ski21.jpg',
        '/photos/ski5.jpg',
        '/photos/ski8.jpg',
        '/photos/ski9.jpg'
      ]
    },
    {
      title: 'AUTO',
      id: 'auto',
      images: [
        '/photos/auto2.jpeg',
        '/photos/auto1.jpeg',
        '/photos/auto4.jpeg',
        '/photos/auto5.png',
        '/photos/auto6.png',
        '/photos/auto7.png',
        '/photos/auto8.png',
        '/photos/auto9.jpg'
      ]
    },
    {
      title: 'LIFESTYLE',
      id: 'lifestyle',
      images: [
        '/photos/life22.jpg',
        '/photos/life4.jpg',
        '/photos/life3.jpg',
        '/photos/life25.jpg',
        '/photos/life16.jpg',
        '/photos/life26.jpg',
        '/photos/life20.jpg',
        '/photos/life19.jpg',
        '/photos/life21.jpg',
        '/photos/life24.jpg',
        '/photos/cycle39.jpg',
        '/photos/life18.jpg',
        '/photos/life6.jpg',
        '/photos/life15.jpg',
        '/photos/life23.jpg',
        '/photos/life12.jpg',
        '/photos/life1.jpg',
        '/photos/life11.jpg',
        '/photos/life10.jpg',
      ]
    },
    {
      title: 'CULINARY',
      id: 'culinary',
      images: [
        '/photos/food1.jpg',
        '/photos/food5.jpg',
        '/photos/food3.jpg',
        '/photos/food2.jpg',
        '/photos/food4.jpg',
        '/photos/food6.jpg',
      ]
    }
  ];

  const openLightbox = (images, index) => {
    setCurrentCategory(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const sectionStyles = css`
    padding: 7rem 1rem 4rem;
    background:
      radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99, 49, 251, 0.35), transparent 70%),
      #01010D;
    min-height: 100vh;
    overflow-x: hidden;

    @media (min-width: 768px) {
      padding: 8rem 1.5rem 5rem;
    }

    @media (min-width: 1024px) {
      padding: 9rem 1.5rem 6rem;
    }
  `;

  const containerStyles = css`
    max-width: 1280px;
    margin: 0 auto;
  `;

  const headerWrapStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const headingStyles = css`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  color: #FFFFFF;
  margin: 0;
`;

const eyebrowStyles = css`
  font-size: 0.75rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #88F9C0;
  margin: 0;
`;

  const categoryStyles = (idx) => css`
    position: relative;
    margin-bottom: 6rem;

    &::before {
      content: '';
      position: absolute;
      top: -10%;
      ${idx % 2 === 0 ? 'right: -20%;' : 'left: -20%;'}
      width: 70%;
      height: 120%;
      background: radial-gradient(circle, rgba(99, 49, 251, 0.25), transparent 70%);
      pointer-events: none;
      z-index: 0;
    }

    @media (min-width: 1024px) {
      margin-bottom: 9rem;
    }
  `;

  const categoryHeaderStyles = css`
    position: relative;
    z-index: 1;
    margin-bottom: 2.5rem;
  `;

  const categoryMetaStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.75rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    font-size: 0.75rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #9A9AB5;
  `;

  const categoryIndexStyles = css`
    color: #88F9C0;
  `;

  const categoryTitleStyles = css`
    font-size: clamp(3.5rem, 11vw, 8rem);
    font-weight: 900;
    line-height: 0.9;
    letter-spacing: -0.03em;
    text-transform: uppercase;
    color: transparent;
    -webkit-text-stroke: 1px rgba(255, 255, 255, 0.45);
    margin: 0;
  `;

  const gridStyles = css`
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
    }

    @media (min-width: 1024px) {
      gap: 1.25rem;
    }
  `;

  const imageContainerStyles = css`
    position: relative;
    aspect-ratio: 4/5;
    overflow: hidden;
    border-radius: 16px;
    cursor: pointer;
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s cubic-bezier(0.22, 1, 0.36, 1);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 0 40px rgba(99, 49, 251, 0.35);
    }
  `;

  const imageStyles = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);

    &:hover {
      transform: scale(1.05);
    }
  `;

  const viewAllWrapStyles = css`
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: center;
    margin-top: 2.5rem;
  `;

  const viewAllStyles = css`
    padding: 0.75rem 1.5rem;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: transparent;
    color: #FFFFFF;
    font-size: 0.8rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.4s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.4s cubic-bezier(0.22, 1, 0.36, 1);

    &:hover {
      background: #6331FB;
      border-color: #6331FB;
    }
  `;

  return (
    <section css={sectionStyles}>
      <div css={containerStyles}>
        <FadeIn>
          <div css={headerWrapStyles}>
            <h2 css={headingStyles}>Photo</h2>
            <p css={eyebrowStyles}>Selected work</p>
          </div>
        </FadeIn>

        {categories.map((category, idx) => (
          <FadeIn key={idx} delay={idx * 0.1}>
            <div css={categoryStyles(idx)} id={category.id}>
              <div css={categoryHeaderStyles}>
                <div css={categoryMetaStyles}>
                  <span css={categoryIndexStyles}>{String(idx + 1).padStart(2, '0')}</span>
                  <span>{category.images.length} photos</span>
                </div>
                <h3 css={categoryTitleStyles}>{category.title}</h3>
              </div>

              <div css={gridStyles}>
                {category.images.slice(0, PREVIEW_COUNT).map((image, imgIdx) => (
                  <div
                    key={imgIdx}
                    css={imageContainerStyles}
                    onClick={() => openLightbox(category.images, imgIdx)}
                  >
                    <img
                      src={image}
                      alt={`${category.title} ${imgIdx + 1}`}
                      css={imageStyles}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>

              {category.images.length > PREVIEW_COUNT && (
                <div css={viewAllWrapStyles}>
                  <button
                    css={viewAllStyles}
                    onClick={() => openLightbox(category.images, 0)}
                  >
                    View all {category.title} photos
                  </button>
                </div>
              )}
            </div>
          </FadeIn>
        ))}
      </div>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={currentCategory.map(src => ({ src }))}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, 0.95)" }
        }}
        carousel={{ finite: false }}
        controller={{ closeOnBackdropClick: true }}
      />
    </section>
  );
};

const HomePage = () => {
  return (
    <div style={{ minHeight: '100vh', background: '#01010D', color: '#FFFFFF' }}>
      <Navigation />
      <Hero />
      <div css={css`
        @media (max-width: 768px) {
          display: none;
        }
      `}>
      </div>
    </div>
  );
};

// PHOTOS PAGE
const PhotosPage = () => {
  return (
    <div style={{ minHeight: '100vh', background: '#000', color: 'white' }}>
      <Navigation />
      <PortfolioGrid />
      <Footer />
    </div>
  );
};

// VIDEO PAGE
const VideoPage = () => {
  const sectionStyles = css`
    padding: 7rem 1rem 3rem;
    background:
      radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99, 49, 251, 0.35), transparent 70%),
      #01010D;
    min-height: 100vh;

    @media (min-width: 768px) {
      padding: 8rem 1.5rem 5rem;
    }

    @media (min-width: 1024px) {
      padding: 9rem 1.5rem 6rem;
    }
  `;

  const containerStyles = css`
    max-width: 1280px;
    margin: 0 auto;
  `;

  const headerWrapStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 1rem;
    padding-bottom: 1rem;
    margin-bottom: 2rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  `;

  const headingStyles = css`
    font-size: clamp(1.75rem, 4vw, 2.5rem);
    font-weight: 900;
    line-height: 1;
    letter-spacing: -0.03em;
    text-transform: uppercase;
    color: #FFFFFF;
    margin: 0;
  `;

  const eyebrowStyles = css`
    font-size: 0.75rem;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: #88F9C0;
    margin: 0;
  `;

  const subHeadingStyles = css`
  font-size: clamp(2.5rem, 7vw, 5rem);
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  text-align: center;
  color: transparent;
  -webkit-text-stroke: 1px rgba(255, 255, 255, 0.45);
  margin: 4rem 0 2.5rem;

  @media (min-width: 1024px) {
    margin: 5rem 0 3rem;
  }
`;

  const videoContainerStyles = css`
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 100%;
    margin: 0 auto;
    background: black;

    @media (min-width: 768px) {
      padding-bottom: 42.1875%;
      max-width: 75%;
    }

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  `;

  return (
    <div style={{ minHeight: '100vh', background: '#01010D' }}>
      <Navigation />
      <section css={sectionStyles}>
        <div css={containerStyles}>
          <FadeIn>
            <div css={headerWrapStyles}>
              <h2 css={headingStyles}>Video</h2>
              <p css={eyebrowStyles}>Showreel &amp; social</p>
            </div>
          </FadeIn>
          
          <FadeIn delay={0.2}>
            <div css={videoContainerStyles}>
              <iframe 
                src="https://player.vimeo.com/video/1051705822?badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                title="Showreel"
              />
            </div>
          </FadeIn>

          {/* Social Reels Section */}
          <FadeIn delay={0.3}>
            <h3 css={subHeadingStyles}>Social Reels</h3>
          </FadeIn>

          <FadeIn delay={0.4}>
          <div style={{ marginTop: '3rem', maxWidth: '1000px', margin: '3rem auto 0', padding: '0 1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '2rem' }}>
              {/* Reel 1 */}
              <div style={{ maxWidth: '100%' }}>
                <div style={{ padding: '177.78% 0 0 0', position: 'relative', background: 'black' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1207570210?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="Slowing things down a bit in Nice"
                  />
                </div>
              </div>

              {/* Reel 2 */}
              <div style={{ maxWidth: '100%' }}>
                <div style={{ padding: '177.78% 0 0 0', position: 'relative', background: 'black' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1207573810?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="This is what it means ❤️"
                  />
                </div>
              </div>

              {/* Reel 3 */}
              <div style={{ maxWidth: '100%' }}>
                <div style={{ padding: '177.78% 0 0 0', position: 'relative', background: 'black' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1207574309?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="Round 7 of the summer series"
                  />
                </div>
              </div>
              {/* Reel 4 */}
              <div style={{ maxWidth: '100%' }}>
                <div style={{ padding: '177.78% 0 0 0', position: 'relative', background: 'black' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1207579381?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="Groupama FDJ - Couchview"
                  />
                </div>
              </div>

              {/* Reel 5 */}
              <div style={{ maxWidth: '100%' }}>
                <div style={{ padding: '177.78% 0 0 0', position: 'relative', background: 'black' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1207579524?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="Lachlan Morton Unbound - EF Pro Cycling"
                  />
                </div>
              </div>

              {/* Reel 6 */}
              <div style={{ maxWidth: '100%' }}>
                <div style={{ padding: '177.78% 0 0 0', position: 'relative', background: 'black' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1207579916?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="Yuma - Maap"
                  />
                </div>
              </div>
              {/* Reel 7 */}
              <div style={{ maxWidth: '100%' }}>
                <div style={{ padding: '177.78% 0 0 0', position: 'relative', background: 'black' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1207582505?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="T Shirt Ride - Team Best"
                  />
                </div>
              </div>

              {/* Reel 8 */}
              <div style={{ maxWidth: '100%' }}>
                <div style={{ padding: '177.78% 0 0 0', position: 'relative', background: 'black' }}>
                  <iframe 
                    src="https://player.vimeo.com/video/1207582657?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="Flanders - Couchview"
                  />
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
        </div>
      </section>
      <Footer />
    </div>
  );
};


//Events Page
const events = [
  {
    title: 'Filthy 50 2026',
    date: 'Sat, Oct 31',
    cover: '/photos/f50jpc.jpg',
    url: 'https://jamesphotographyco.pixieset.com/filthy502026/',
  },

  {
    title: 'Sea Otter 2025',
    date: 'Sat, Apr 12',
    cover: '/photos/seaotter1.jpg',
    url: 'https://jamesphotographyco.pixieset.com/seaotter2025/',
  },
  
];

const EventsPage = () => {
  const pageStyles = css`
    min-height: 100vh;
    background: #01010D;
  `;

  const containerStyles = css`
    max-width: 1280px;
    margin: 0 auto;
    padding: 7rem 1rem 5rem;

    @media (min-width: 768px) {
      padding: 8rem 1.5rem 5rem;
    }
  `;

  const gridStyles = css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
  `;

  const headerWrapStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const headingStyles = css`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  color: #FFFFFF;
  margin: 0;
`;

const eyebrowStyles = css`
  font-size: 0.75rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #88F9C0;
  margin: 0;
`;

const tileStyles = css`
  position: relative;
  display: block;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  border-radius: 4px;
  background: #111;
  text-decoration: none;
  color: white;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 55%);
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const tileTitleStyles = css`
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  padding-bottom: 1rem;
`;

  const overlayStyles = css`
    position: absolute;
    left: 1.5rem;
    right: 1.5rem;
    bottom: 1.5rem;
    z-index: 1;
  `;

  const dateStyles = css`
    margin-top: 0.35rem;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    opacity: 0.85;
    color: #88F9C0;
  `;

  return (
    <div css={pageStyles}>
      <Navigation />

      <div css={containerStyles}>
        <div css={headerWrapStyles}>
          <h1 css={headingStyles}>Events</h1>
          <p css={eyebrowStyles}>Race days &amp; rides</p>
        </div>

        <div css={gridStyles}>
          {events.map((event) => (
            <a
              key={event.title}
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              css={tileStyles}
            >
              <img src={event.cover} alt={event.title} />
              <div css={overlayStyles}>
                <div css={tileTitleStyles}>{event.title}</div>
                <div css={dateStyles}>{event.date}</div>
              </div>
            </a>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

// WEDDINGS PAGE
const WeddingsPage = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentImages, setCurrentImages] = useState([]);

  const weddingPhotos = [
    { name: 'nakamura', count: 5, horizontalIndices: [3] },
    { name: 'lewis', count: 9, horizontalIndices: [2,6]},
    { name: 'kranz', count: 4, horizontalIndices: [1] },
    { name: 'warren', count: 6, horizontalIndices: [] },
    { name: 'rogers', count: 3, horizontalIndices: [] },
    { name: 'johnson', count: 3, horizontalIndices: [] },
    { name: 'gonzalez', count: 3, horizontalIndices: [0] },
    { name: 'israel', count: 2, horizontalIndices: [0] },
    { name: 'delmar', count: 3, horizontalIndices: [] }
  ];

  const allPhotos = (() => {
    const photos = weddingPhotos.flatMap((wedding) => {
      return Array.from({ length: wedding.count }, (_, i) => ({
        src: `/photos/weddings/wedding-${weddingPhotos.indexOf(wedding) + 1}-${wedding.name}/${wedding.name}-${i + 1}${wedding.horizontalIndices.includes(i) ? '-horizontal' : ''}.jpg`,
        isHorizontal: wedding.horizontalIndices.includes(i)
      }));
    });
  
    const horizontal = photos.filter(p => p.isHorizontal);
    const vertical = photos.filter(p => !p.isHorizontal);
  
    const result = [];
    let vIndex = 0;
    let hIndex = 0;
  
    while (vIndex < vertical.length || hIndex < horizontal.length) {
      for (let i = 0; i < 3 && vIndex < vertical.length; i++) {
        result.push(vertical[vIndex++]);
      }
      if (hIndex < horizontal.length) {
        result.push(horizontal[hIndex++]);
      }
    }
  
    return result;
  })();

  const openGallery = (images, index = 0) => {
    setCurrentImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const pageStyles = css`
    min-height: 100vh;
    background: #01010D;
  `;

  const containerStyles = css`
    max-width: 1280px;
    margin: 0 auto;
    padding: 5rem 1rem;

    @media (min-width: 768px) {
      padding: 6rem 1.5rem;
    }

    @media (min-width: 1024px) {
      padding: 7rem 1.5rem;
    }
  `;

  const headerWrapStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const headingStyles = css`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  color: #FFFFFF;
  margin: 0;
`;

const eyebrowStyles = css`
  font-size: 0.75rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #88F9C0;
  margin: 0;
`;

  const gridStyles = css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }

    > div:has(> div[data-horizontal="true"]) {
      grid-column: 1 / -1;
    }
  `;

  const photoContainerStyles = (isHorizontal) => css`
    position: relative;
    aspect-ratio: ${isHorizontal ? '16 / 9' : '3 / 4'};
    overflow: hidden;
    cursor: pointer;
    border-radius: 4px;
    background: #14142A;

    &:hover img {
      transform: scale(1.05);
    }
  `;

  const photoImageStyles = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  `;

  return (
    <div css={pageStyles}>
      <Navigation />
      
      <div css={containerStyles}>
        <FadeIn>
          <div css={headerWrapStyles}>
            <h1 css={headingStyles}>Weddings</h1>
            <p css={eyebrowStyles}>I'd be happy to shoot yours :)</p>
          </div>
        </FadeIn>

        <div css={gridStyles}>
          {allPhotos.map((photo, idx) => (
            <FadeIn key={idx} delay={Math.min(idx * 0.05, 0.5)}>
              <div
                css={photoContainerStyles(photo.isHorizontal)}
                data-horizontal={photo.isHorizontal}
                onClick={() => openGallery(allPhotos.map(p => p.src), idx)}
              >
                <img
                  src={photo.src}
                  alt={`Wedding ${idx + 1}`}
                  css={photoImageStyles}
                />
              </div>
            </FadeIn>
          ))}
        </div>
      </div>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={currentImages.map(src => ({ src }))}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, 0.95)" }
        }}
        carousel={{ finite: false }}
        controller={{ closeOnBackdropClick: true }}
      />

      <Footer />
    </div>
  );
};


// CONTACT PAGE
const ContactPage = () => {
  const pageStyles = css`
    min-height: 100vh;
    background: #01010D;
  `;

  return (
    <div css={pageStyles}>
      <Navigation />
      <Contact />
      <Footer />
    </div>
  );
};

// Main App with Router
export default function PhotographyWebsite() {
  return (
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/photos" element={<PhotosPage />} />
          <Route path="/video" element={<VideoPage />} />
          <Route path="/weddings" element={<WeddingsPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
      </Router>
  );
}